import { BoardSnapshot } from './board-snapshot';
import { Card } from './card/card';
import { Pile } from './card/pile';
import { GameLogicDepth } from './game-logic-depth';
import { PileId } from './logic-types';

export class Board {
  private snapshot: BoardSnapshot;

  public get foundations(): Pile[] {
    return this.snapshot.foundations;
  }

  public get tableaus(): Pile[] {
    return this.snapshot.tableaus;
  }

  public get deck(): Pile {
    return this.snapshot.deck;
  }

  public get waste(): Pile {
    return this.snapshot.waste;
  }

  public get boardSnapshot(): BoardSnapshot {
    return this.snapshot;
  }

  public set boardSnapshot(snapshot: BoardSnapshot) {
    this.snapshot = snapshot;
  }

  constructor(foundations: Pile[], tableaus: Pile[], deck: Pile, waste: Pile) {
    this.snapshot = new BoardSnapshot(foundations, tableaus, deck, waste);
  }

  public getPileByCardIndex(index: number): Pile {
    for (const pile of this.foundations) {
      if (pile.containsCardIndex(index)) {
        return pile;
      }
    }

    for (const pile of this.tableaus) {
      if (pile.containsCardIndex(index)) {
        return pile;
      }
    }

    if (this.deck.containsCardIndex(index)) {
      return this.deck;
    }

    if (this.waste.containsCardIndex(index)) {
      return this.waste;
    }

    throw new Error(`Pile not found that contains card with index: ${index}`);
  }

  public getPileByCardId(cardId: string): Pile | undefined {
    for (const pile of this.foundations) {
      if (pile.containsCardId(cardId)) {
        return pile;
      }
    }

    for (const pile of this.tableaus) {
      if (pile.containsCardId(cardId)) {
        return pile;
      }
    }

    if (this.deck.containsCardId(cardId)) {
      return this.deck;
    }

    if (this.waste.containsCardId(cardId)) {
      return this.waste;
    }

    return undefined;
  }

  public getPileByPileId(pileId: PileId): Pile {
    const foundInFoundations = this.foundations.find((pile) => pile.id === pileId);
    if (foundInFoundations !== undefined) {
      return foundInFoundations;
    }

    const foundInTableaus = this.tableaus.find((pile) => pile.id === pileId);
    if (foundInTableaus !== undefined) {
      return foundInTableaus;
    }

    if (PileId.Deck === pileId) {
      return this.deck;
    }

    if (PileId.Waste === pileId) {
      return this.waste;
    }

    throw new Error(`Pile not found with id: ${pileId}`);
  }

  public getCardByCardId(cardId: string): Card | undefined {
    const pile = this.getPileByCardId(cardId);
    return pile?.getCardByCardId(cardId);
  }

  public getCardByCardIndex(index: number): Card | undefined {
    const pile = this.getPileByCardIndex(index);
    return pile.getCardByCardIndex(index);
  }

  public lastDeckCard(): Card | undefined {
    return this.deck.lastCard;
  }

  public getAllCards(): Card[] {
    const allCards: Card[] = [];

    this.deck.combine(allCards);
    this.waste.combine(allCards);

    for (const pile of this.tableaus) {
      pile.combine(allCards);
    }

    for (const pile of this.foundations) {
      pile.combine(allCards);
    }

    return allCards;
  }

  public parentCard(childCard: Card): Card | undefined {
    const pile = this.getPileByCardIndex(childCard.index);
    return pile.parentCard(childCard);
  }

  public printLog(gameLogicDepth: GameLogicDepth) {
    console.log(`==========`);
    this.foundations.forEach((pile) => pile.printLog(gameLogicDepth));
    console.log(`----------`);
    this.tableaus.forEach((pile) => pile.printLog(gameLogicDepth));
    console.log(`----------`);
    this.deck.printLog(gameLogicDepth);
    this.waste.printLog(gameLogicDepth);
    console.log(`==========`);
  }
}
